import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

export interface CrmTab {
  id: string;
  path: string;
  label: string;
}

interface TabsState {
  tabs: CrmTab[];
  activeTabId: string;
  /** Stack of recently closed tabs for Cmd+Shift+E reopen */
  closedStack: CrmTab[];
  /**
   * Last search string (e.g. filters) seen at each pathname, independent of
   * tabs — lets a bare navigation (left nav, search, ⌘E on an existing path)
   * restore "wherever that section was left" instead of resetting to
   * defaults. Keyed by pathname only, so it's shared across tabs on the same
   * section (matches "leaving and returning to a section" in the spec).
   */
  lastSearchByPath: Record<string, string>;

  openNewTab: () => string;
  /** Returns path to navigate to after close, or null if no remaining tabs */
  closeTab: (id: string) => string | null;
  /** Close all tabs at once */
  closeAllTabs: () => void;
  setActiveTab: (id: string) => void;
  /** Relabel the tab whose path matches (e.g. to show an AI CIO chat title). */
  setTabLabel: (path: string, label: string) => void;
  /**
   * Reflect a navigation in the tab strip:
   * - repoint the current tab to the new page (duplicate paths allowed);
   * - if there are no tabs yet, seed one for the page.
   */
  syncActiveTab: (path: string, label: string) => void;
  /** Record the current search string for a pathname (`''` for none — a
   * deliberate "Reset" is a state worth remembering too). */
  rememberSearch: (pathname: string, searchStr: string) => void;
  reorderTabs: (fromIndex: number, toIndex: number) => void;
  reopenLastClosed: () => string | null;
}

let _seq = 0;
function uid(): string {
  return `t${Date.now().toString()}-${(++_seq).toString()}`;
}

export const useTabsStore = create<TabsState>()(
  persist(
    (set, get) => ({
      tabs: [],
      activeTabId: '',
      closedStack: [],
      lastSearchByPath: {},

      openNewTab() {
        const id = uid();
        set((s) => ({
          tabs: [...s.tabs, { id, path: '/new-tab', label: 'New Tab' }],
          activeTabId: id,
        }));
        return id;
      },

      closeTab(id) {
        const { tabs, activeTabId, closedStack } = get();
        const idx = tabs.findIndex((t) => t.id === id);
        if (idx === -1) return null;

        const closed = tabs[idx]; // idx !== -1 checked above
        if (!closed) return null;
        const remaining = tabs.filter((t) => t.id !== id);

        let nextActiveId = '';
        let navigateTo: string | null = null;

        if (remaining.length > 0) {
          if (activeTabId === id) {
            const nextIdx = Math.min(idx, remaining.length - 1);
            const next = remaining[nextIdx];
            if (next) {
              nextActiveId = next.id;
              navigateTo = next.path;
            }
          } else {
            nextActiveId = activeTabId;
          }
        }

        set({
          tabs: remaining,
          activeTabId: nextActiveId,
          closedStack: [closed, ...closedStack].slice(0, 10),
        });
        return navigateTo;
      },

      closeAllTabs() {
        set({ tabs: [], activeTabId: '' });
      },

      setActiveTab(id) {
        set({ activeTabId: id });
      },

      setTabLabel(path, label) {
        set((s) => ({
          tabs: s.tabs.map((t) => (t.path === path && t.label !== label ? { ...t, label } : t)),
        }));
      },

      syncActiveTab(path, label) {
        const { tabs, activeTabId } = get();
        const active = tabs.find((t) => t.id === activeTabId);

        // Already on a tab showing this page — keep it. This is what lets you
        // click between two same-path tabs without being bounced to the first,
        // and short-circuits the redundant work of re-selecting the same page.
        if (active && active.path === path) return;

        // No tab yet (fresh load, or after Close All / closing the last tab):
        // seed one so navigation always has a tab to land in.
        if (!activeTabId || tabs.length === 0) {
          const id = uid();
          set({ tabs: [...tabs, { id, path, label }], activeTabId: id });
          return;
        }

        // Switch the current tab's page in place (no new tab). Duplicate paths
        // are allowed — e.g. two Dashboard tabs opened via ⌘E.
        set({ tabs: tabs.map((t) => (t.id === activeTabId ? { ...t, path, label } : t)) });
      },

      rememberSearch(pathname, searchStr) {
        const { lastSearchByPath } = get();
        if (lastSearchByPath[pathname] === searchStr) return;
        set({ lastSearchByPath: { ...lastSearchByPath, [pathname]: searchStr } });
      },

      reorderTabs(fromIndex, toIndex) {
        const arr = [...get().tabs];
        const [item] = arr.splice(fromIndex, 1);
        if (item !== undefined) arr.splice(toIndex, 0, item);
        set({ tabs: arr });
      },

      reopenLastClosed() {
        const { closedStack } = get();
        if (!closedStack.length) return null;
        const tab = closedStack[0];
        if (!tab) return null;
        const rest = closedStack.slice(1);
        const id = uid();
        const restored: CrmTab = { ...tab, id };
        set((s) => ({
          tabs: [...s.tabs, restored],
          activeTabId: id,
          closedStack: rest,
        }));
        return restored.path;
      },
    }),
    {
      name: 'crm-tabs',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (s) => ({
        tabs: s.tabs,
        activeTabId: s.activeTabId,
        lastSearchByPath: s.lastSearchByPath,
      }),
      merge: (persisted, current) => {
        const saved = (persisted ?? {}) as Partial<TabsState>;
        const tabs = saved.tabs ?? [];

        let activeTabId = '';
        if (saved.activeTabId && tabs.some((t) => t.id === saved.activeTabId)) {
          activeTabId = saved.activeTabId;
        } else if (tabs[0]) {
          activeTabId = tabs[0].id;
        }

        return { ...current, ...saved, tabs, activeTabId };
      },
    },
  ),
);
